import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { Customer, CustomerRequest, defaultCustomer } from "./Customer";
import {
  emailRegExp,
  passwordRule,
} from "../../supportFunctions/ValidationHelpers";
import {
  createCustomerMutation,
  setCustomerLocationManagement,
  updateCustomerMutation,
  updateCustomerStatusMutation,
} from "../../supportFunctions/graphql/CustomerQueries";
import fieldTrim from "../../supportFunctions/FieldTrim";
import {
  Card,
  BaseButton,
  Input,
  Modal,
  UnsavedChangesPrompt,
  RadioButtonGroup,
} from "@velocity-ehs/component-library";
import FormSkeleton from "../../sharedComponents/FormSkeleton";
import { useTranslation } from "react-i18next";
import GatewayService from "../../../services/GatewayService";

export enum Mode {
  Add,
  Edit,
  View,
}

// Create a type alias to break the circular reference
type CustomerFormMethods = UseFormReturn<any, unknown>;

interface Props {
  mode: Mode;
  customer?: Customer;
  isLoading?: boolean;
  onSaveCustomer?: () => void;
  formMethods?: CustomerFormMethods;
}

const CustomerForm: React.FC<Props> = ({
  mode = Mode.View,
  customer = defaultCustomer,
  isLoading = false,
  onSaveCustomer,
  formMethods,
}) => {
  let history = useHistory();
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const modificationMode: Mode = mode;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isUpdatingStatus, setisUpdatingStatus] = useState(false);

  const {
    reset,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = formMethods;

  const watchFullyIntegrated = watch("fullyIntegrated");

  const saveUpdateCustomer = async (
    customerData: CustomerRequest,
    formValues: Customer
  ) => {
    if (modificationMode === Mode.Edit || modificationMode === Mode.View) {
      try {
        const {
          id,
          name,
          velocityEhsCustomerNumber,
          fullyIntegrated,
          locationManagementEnabled,
          orgHierarchyManagementEnabled,
        } = customerData;

        await GatewayService.api.post(
          "",
          updateCustomerMutation({
            id,
            name,
            velocityEhsCustomerNumber,
            fullyIntegrated,
          })
        );

        await GatewayService.api.post(
          "",
          setCustomerLocationManagement({
            id,
            locationManagementEnabled,
            orgHierarchyManagementEnabled,
          })
        );

        history.replace(`/customer/${customer?.id}/edit`);
        showSnackbar?.(SnackbarType.Success, t("cutomer_successfully_updated"));
      } catch (error: any) {
        attemptShowErrorsInSnackbar(showSnackbar, error);
      } finally {
        reset(formValues);
      }
    } else {
      try {
        await GatewayService.api.post("", createCustomerMutation(customerData));
        reset(formValues);
        showSnackbar?.(
          SnackbarType.Success,
          `Customer and user ${customerData.admin?.firstName} ${customerData.admin?.lastName} have been successfully added.`
        );

        history.push("/customer");
      } catch (error: any) {
        attemptShowErrorsInSnackbar(showSnackbar, error);
      }
    }
  };

  const activateCustomer = () => {
    if (customer) {
      setisUpdatingStatus(true);
      GatewayService.api
        .post("", updateCustomerStatusMutation(customer?.id, true))
        .then(() => {
          if (showSnackbar) {
            showSnackbar(
              SnackbarType.Success,
              t("customer_activated_successfully")
            );
          }
          history.push("/customer");
        })
        .catch((error: any) => {
          /* eslint-disable-next-line no-console */
          console.log(error);
          attemptShowErrorsInSnackbar(showSnackbar, error);
          setisUpdatingStatus(false);
        });
      return false;
    }
  };

  const deleteEntity = () => {
    if (customer) {
      setisUpdatingStatus(true);
      setShowDeleteConfirmation(false);
      GatewayService.api
        .post("", updateCustomerStatusMutation(customer?.id, false))
        .then(() => {
          if (showSnackbar) {
            showSnackbar(
              SnackbarType.Success,
              t("customer_inactivated_successfully")
            );
          }
          history.push("/customer");
        })
        .catch((error: any) => {
          /* eslint-disable-next-line no-console */
          console.log(error);
          attemptShowErrorsInSnackbar(showSnackbar, error);
          setisUpdatingStatus(false);
        });
      return false;
    }
  };

  const onSubmit = useCallback(
    async (formValues: Customer) => {
      // trim all fields on customer form data
      const trimmedFormValues = fieldTrim({ formData: formValues });
      // cname should only be modifiable in add mode
      const cname =
        modificationMode === Mode.Add
          ? formValues.customerDomainName
          : customer?.customerDomainName;
      const data = {
        name: trimmedFormValues.name,
        velocityEhsCustomerNumber: trimmedFormValues.velocityEhsCustomerNumber,
        active: trimmedFormValues.active,
        customerDomainName: cname?.trim(),
        id: customer?.id,
        locationManagementEnabled: trimmedFormValues.locationManagementEnabled,
        orgHierarchyManagementEnabled:
          trimmedFormValues.orgHierarchyManagementEnabled,
        fullyIntegrated: trimmedFormValues.fullyIntegrated,
      } as CustomerRequest;

      if (
        modificationMode === Mode.Add &&
        trimmedFormValues.adminPassword &&
        trimmedFormValues.adminFirstName &&
        trimmedFormValues.adminLastName &&
        trimmedFormValues.adminEmail &&
        trimmedFormValues.adminUsername &&
        trimmedFormValues.adminDisplayName
      ) {
        data.admin = {
          firstName: trimmedFormValues.adminFirstName,
          lastName: trimmedFormValues.adminLastName,
          email: trimmedFormValues.adminEmail,
          phone: trimmedFormValues.adminPhone,
          username: trimmedFormValues.adminUsername,
          password: trimmedFormValues.adminPassword,
          displayName: trimmedFormValues.adminDisplayName,
        };
      } else if (modificationMode === Mode.Add) {
        return;
      }
      await saveUpdateCustomer(data, trimmedFormValues);
      onSaveCustomer && onSaveCustomer();
    },
    // eslint-disable-next-line
    [customer]
  );

  useEffect(() => {
    if (watchFullyIntegrated === false) {
      setValue("locationManagementEnabled", false);
    }
  }, [watchFullyIntegrated, setValue]);

  useEffect(() => {
    if (customer) {
      reset(customer);
    }
  }, [customer, reset]);

  const usersInOrg = (
    <>
      <Input
        control={control}
        name="numberOfUsers"
        label={t("customer_Customer_Users_In_Org_Text")}
        disabled
      />
    </>
  );

  const customerForm = (
    <>
      <Input
        control={control}
        name="name"
        label={t("universal_Name")}
        rules={{
          required: t("customer_Form_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors ? errors.name?.message : undefined}
        disabled={modificationMode === Mode.View}
      />
      <Input
        control={control}
        name="customerDomainName"
        label={t("universal_Domain")}
        helpText={
          t("customer_Form_Domain_Help_Text") + ": A-Z, a-z, 0-9, -, or _" // TODO: Translate A-Z, a-z, 0-9, -, or _ (leave as is for now)
        }
        rules={{
          required: t("required_message_domain"),
          maxLength: {
            value: 64,
            message: t("customer_Form_Domain_Max_Length_validation"),
          },
          pattern: {
            value: /^[A-Za-z0-9-_]+$/i,
            message: t("customer_Form_Domain_Invalid"),
          },
        }}
        disabled={modificationMode !== Mode.Add}
        errorMessage={errors && errors.customerDomainName?.message}
      />
      <Input
        control={control}
        name="velocityEhsCustomerNumber"
        label={t("velocityEhsCustomerNumber")}
        rules={{
          required: t("velocityEhsCustomerNumber_required"),
        }}
        errorMessage={
          errors ? errors.velocityEhsCustomerNumber?.message : undefined
        }
        disabled={modificationMode === Mode.View}
      />
      {modificationMode !== Mode.Add && usersInOrg}
      <RadioButtonGroup
        control={control}
        name="orgHierarchyManagementEnabled"
        label={t("customer_can_manage_hierarchies")}
        options={[
          {
            label: t("universal_yes"),
            value: true,
          },
          {
            label: t("universal_no"),
            value: false,
          },
        ]}
        readOnly={modificationMode === Mode.View}
      />
      <RadioButtonGroup
        control={control}
        name="fullyIntegrated"
        label={t("customer_fully_integrated")}
        options={[
          {
            label: t("universal_yes"),
            value: true,
          },
          {
            label: t("universal_no"),
            value: false,
          },
        ]}
        readOnly={modificationMode === Mode.View}
      />
      <RadioButtonGroup
        control={control}
        name="locationManagementEnabled"
        label={t("customer_can_manage_locations")}
        options={[
          {
            label: t("universal_yes"),
            value: true,
          },
          {
            label: t("universal_no"),
            value: false,
          },
        ]}
        readOnly={
          modificationMode === Mode.View || watchFullyIntegrated === false
        }
      />
    </>
  );

  const adminUserForm = (
    <>
      <Input
        control={control}
        name="adminDisplayName"
        label={t("universal_display_name")}
        rules={{
          required: t("form_message_Display_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        disabled={modificationMode === Mode.View}
        errorMessage={errors ? errors.adminDisplayName?.message : undefined}
      />
      <Input
        control={control}
        name="adminFirstName"
        label={t("universal_First_Name")}
        rules={{
          required: t("customer_Form_Admin_User_First_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        disabled={modificationMode === Mode.View}
        errorMessage={errors ? errors.adminFirstName?.message : undefined}
      />
      <Input
        control={control}
        name="adminLastName"
        label={t("universal_Last_Name")}
        rules={{
          required: t("customer_Form_Admin_User_Last_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        disabled={modificationMode === Mode.View}
        errorMessage={errors && errors.adminLastName?.message}
      />
      <Input
        control={control}
        name="adminEmail"
        label={t("universal_Email")}
        rules={{
          required: t("customer_Form_Admin_User_Email_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
          pattern: {
            value: emailRegExp,
            message: t("message_invalid_email_address"),
          },
        }}
        errorMessage={errors ? errors.adminEmail?.message : undefined}
      />
      <Input
        control={control}
        name="adminPhone"
        label={t("universal_Phone")}
        rules={{
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.adminPhone?.message}
      />
      <Input
        control={control}
        name="adminUsername"
        label={t("universal_Username")}
        rules={{
          required: t("customer_Form_Admin_User_Username_Required"),
          pattern: {
            value: /^\s*[^+\\;,]+\s*$/i,
            message: t("message_invalid_username"),
          },
          maxLength: {
            value: 64,
            message: t(
              "customer_Form_Admin_User_Username_Max_Length_Validation"
            ),
          },
        }}
        disabled={modificationMode === Mode.View}
        errorMessage={errors && errors.adminUsername?.message}
      />
      <Input
        control={control}
        name="adminPassword"
        label={t("universal_Password")}
        helpText={t("universal_Password_Requirements")}
        type="password"
        rules={{
          required: t("customer_Form_Admin_User_Password_Required"),
          maxLength: {
            value: 32,
            message: t(
              "customer_Form_Admin_User_Password_Max_Length_Validation"
            ),
          },
          validate: (value) => passwordRule(value),
        }}
        disabled={modificationMode === Mode.View}
        errorMessage={errors && errors.adminPassword?.message}
      />
    </>
  );

  const customerCard = (
    /* 
      NOTE: the padding is added here because these cards get wrapped in a form
      which makes it not respect the margin bottom with this addition.
    */
    <Card
      title={t("customer_Customer_Detail_Text")}
      style={{ paddingBottom: "2px" }}
      headerAction={
        customer?.active ? (
          <BaseButton
            className="p-button-text"
            label={t("universal_Inactivate")}
            visible={modificationMode === Mode.Edit}
            onClick={() => setShowDeleteConfirmation(true)}
          />
        ) : (
          <BaseButton
            className="p-button-text"
            onClick={() => activateCustomer()}
            loading={isUpdatingStatus}
            label={t("universal_Activate")}
            visible={modificationMode === Mode.Edit}
          />
        )
      }
    >
      {isLoading ? <FormSkeleton rows={4} /> : customerForm}
    </Card>
  );

  const adminUserCard = (
    <Card
      title={t("customer_Form_Admin_User")}
      style={{ paddingBottom: "2px" }}
    >
      {adminUserForm}
    </Card>
  );

  return (
    <>
      <form id="customerForm" onSubmit={handleSubmit(onSubmit)}>
        {customerCard}
        {modificationMode === Mode.Add && adminUserCard}
      </form>
      <Modal
        title={t("action_inactivate_customer")}
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        footerButtons={[
          {
            id: "deleteConfirmationButton",
            label: t("universal_Inactivate"),
            onClick: deleteEntity,
            loading: isUpdatingStatus,
            className: "p-button-danger",
          },
          {
            id: "cancelDelete",
            label: t("universal_Cancel"),
            onClick: () => setShowDeleteConfirmation(false),
            className: "p-button-text",
          },
        ]}
        children={<p>{t("message_inactivated_Customer_Users")}</p>}
      />
      <UnsavedChangesPrompt isDirty={isDirty} history={history} />
    </>
  );
};

export default CustomerForm;
